import { useState } from 'react';
import { siteMeta } from '@/mocks/gossipData';

const FORM_URL = 'https://readdy.ai/api/form/dagkt99tto0ceqqnhubg';

const directionOptions = ['综合瓜报（默认）', '影视剧综', '综艺与音乐', '时尚与品牌'];

export default function Subscribe() {
  const [status, setStatus] = useState<'idle' | 'success' | 'error'>('idle');
  const [formError, setFormError] = useState('');
  const [msg, setMsg] = useState('');

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const fd = new FormData(form);

    // 蜜罐：被机器人填了就静默当作成功，不发请求
    const honeypot = String(fd.get('contact_alt') ?? '').trim();
    if (honeypot) {
      setStatus('success');
      setMsg('已收到订阅申请，感谢关注「昨日吃瓜」！');
      form.reset();
      return;
    }

    setStatus('idle');
    setFormError('');
    try {
      const res = await fetch(FORM_URL, {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: new URLSearchParams(fd as unknown as Record<string, string>).toString(),
      });
      const responseText = await res.text();
      let parsed: { code?: string; message?: string; meta?: { message?: string; detail?: string } } | null = null;
      try {
        parsed = responseText ? JSON.parse(responseText) : null;
      } catch {
        parsed = null;
      }
      const serverMsg =
        parsed?.meta?.message || parsed?.meta?.detail || parsed?.message || responseText || '';
      const isSpam = serverMsg.toLowerCase().includes('spam');
      const ok = res.ok && !isSpam && parsed?.code === 'OK';

      if (ok) {
        setStatus('success');
        setMsg('订阅成功，从下一个自然日起，每晚瓜报准时送达你的邮箱。');
        form.reset();
      } else {
        setStatus('error');
        setFormError(
          serverMsg && !isSpam
            ? serverMsg
            : '提交未成功，请检查邮箱格式后重试。',
        );
      }
    } catch {
      setStatus('error');
      setFormError('网络似乎开小差了，请稍后重试。');
    }
  };

  return (
    <section id="subscribe" className="scroll-mt-20 bg-gradient-to-r from-primary-600 via-primary-500 to-accent-600">
      <div className="mx-auto max-w-7xl px-4 py-12 md:px-6 md:py-16">
        <div className="grid grid-cols-1 items-center gap-8 lg:grid-cols-2">
          <div>
            <span className="inline-flex items-center gap-2 rounded-full bg-background-50/15 px-3 py-1 text-xs font-semibold tracking-wide text-background-100">
              <i className="ri-mail-star-line" /> 订阅昨日瓜报
            </span>
            <h2 className="mt-4 font-heading text-2xl font-bold leading-snug text-background-50 md:text-[2rem]">
              每晚一到，昨日瓜料不缺席
            </h2>
            <p className="mt-3 max-w-xl text-sm leading-7 text-background-200/95 md:text-base">
              留下邮箱，每天收到一份编辑部梳理好的昨日瓜报：热搜怎么来的、反转怎么落的、大家又在聊什么，几分钟读完，第二天聊天不掉队。
            </p>
            <ul className="mt-5 flex flex-col gap-2 text-sm text-background-100">
              <li className="flex items-center gap-2">
                <i className="ri-checkbox-circle-fill text-accent-200" /> 每晚 24 点前送达当日瓜报
              </li>
              <li className="flex items-center gap-2">
                <i className="ri-checkbox-circle-fill text-accent-200" /> 重大事件即时速报，不让你错过关键节点
              </li>
              <li className="flex items-center gap-2">
                <i className="ri-checkbox-circle-fill text-accent-200" /> 随时可退订，尊重你的阅读节奏
              </li>
            </ul>
          </div>

          <div className="rounded-2xl bg-background-50 p-5 shadow-soft md:p-7">
            <h3 className="font-heading text-lg font-bold text-foreground-950">免费订阅</h3>
            <p className="mt-1 text-xs text-foreground-500">填好邮箱即可，无需注册账号</p>
            <form data-readdy-form="昨日吃瓜订阅表单" onSubmit={handleSubmit} className="mt-4 flex flex-col gap-3">
              <div className="flex flex-col gap-3 sm:flex-row">
                <div className="flex flex-1 items-center gap-2 rounded-full border border-background-200 bg-background-100 px-4 focus-within:border-primary-400">
                  <i className="ri-mail-line text-base text-foreground-400" />
                  <input
                    type="email"
                    name="email"
                    required
                    placeholder="输入你的邮箱地址"
                    autoComplete="email"
                    className="h-11 w-full bg-transparent text-sm outline-none placeholder:text-foreground-400"
                  />
                </div>
              </div>
              <div className="flex flex-col gap-3 sm:flex-row">
                <div className="flex flex-1 items-center gap-2 rounded-full border border-background-200 bg-background-100 px-4">
                  <i className="ri-apps-2-line text-base text-foreground-400" />
                  <select
                    name="direction"
                    defaultValue={directionOptions[0]}
                    className="h-11 w-full cursor-pointer bg-transparent text-sm outline-none"
                  >
                    {directionOptions.map((d) => (
                      <option key={d} value={d}>
                        {d}
                      </option>
                    ))}
                  </select>
                  <i className="ri-arrow-down-s-line text-base text-foreground-400" />
                </div>
              </div>

              {/* 蜜罐字段（隐藏，用于识别垃圾提交） */}
              <div className="hp-field" aria-hidden="true">
                <input type="text" name="contact_alt" tabIndex={-1} autoComplete="off" readOnly aria-hidden="true" />
              </div>

              <button
                type="submit"
                disabled={status === 'success'}
                className="inline-flex cursor-pointer items-center justify-center gap-2 whitespace-nowrap rounded-full bg-primary-500 px-5 py-3 text-sm font-semibold text-background-50 transition-colors hover:bg-primary-600 disabled:cursor-not-allowed disabled:opacity-70"
              >
                {status === 'success' ? (
                  <>
                    <i className="ri-check-line" /> 订阅成功
                  </>
                ) : (
                  <>
                    立即订阅 <i className="ri-send-plane-line" />
                  </>
                )}
              </button>

              {status === 'success' ? (
                <p className="flex items-start gap-2 rounded-xl bg-secondary-100 px-3 py-2.5 text-sm text-secondary-900">
                  <i className="ri-checkbox-circle-fill mt-0.5 text-secondary-700" /> {msg}
                </p>
              ) : null}
              {status === 'error' ? (
                <p className="flex items-start gap-2 rounded-xl bg-accent-100 px-3 py-2.5 text-sm text-accent-800">
                  <i className="ri-error-warning-fill mt-0.5 text-accent-600" /> {formError}
                </p>
              ) : null}
            </form>
            <p className="mt-3 text-[11px] leading-5 text-foreground-400">
              订阅即代表同意我们以邮件形式向「{siteMeta.brand}」为你发送瓜报内容，你随时可以退订。
            </p>
          </div>
        </div>
      </div>
    </section>
  );
}